import React from "react";
import { Empty, Tag } from "antd";
import Chart from "react-apexcharts";
import moment from "moment";
import { FaUserGraduate } from "react-icons/fa";
import {
  HiOutlineClipboardCheck,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { Card, CardTitle, KpiStatTile, SectionShell } from "../blocks";
import {
  GENDER_META,
  SECTION_COLORS,
  SERIES,
  areaBase,
  axisLabelStyle,
  baseChart,
  baseGrid,
  donutBase,
} from "../chartTheme";

const ACCENT = SECTION_COLORS.students;

const StudentsSection = ({ overview, attendance, distribution }) => {
  // Fill all 14 trailing days so gaps (holidays) render as zero. `markedCount`
  // (students with an attendance record that day) is distinct from
  // `totalStudents` (full roster) — a day only "has data" if something was
  // actually marked, even though totalStudents is always > 0.
  const attendanceDays = Array.from({ length: 14 }, (_, i) => {
    const d = moment()
      .startOf("day")
      .subtract(13 - i, "days");
    const row = (attendance.trend || []).find(
      (t) => t.date === d.format("YYYY-MM-DD")
    );
    return {
      label: d.format("DD MMM"),
      percentage: row ? row.percentage : 0,
      present: row ? row.present : 0,
      markedCount: row ? row.markedCount : 0,
      notMarked: row ? row.notMarked : overview.totalStudents || 0,
      totalStudents: row ? row.totalStudents : overview.totalStudents || 0,
    };
  });
  const hasAttendanceData = attendanceDays.some((d) => d.markedCount > 0);

  const genderSeries = (distribution.byGender || []).map((g) => g.count);
  const hasGenderData = genderSeries.some((v) => v > 0);

  const byClass = distribution.byClass || [];
  const hasClassData = byClass.length > 0;

  const attendanceArea = {
    options: {
      chart: baseChart("iskool360-attendance-trend", "area"),
      colors: [ACCENT],
      ...areaBase,
      xaxis: {
        categories: attendanceDays.map((d) => d.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { ...axisLabelStyle.style, fontSize: "10px" },
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: { formatter: (v) => `${Math.round(v)}%` },
      },
      tooltip: {
        y: {
          formatter: (v, { dataPointIndex }) => {
            const d = attendanceDays[dataPointIndex];
            return `${v}% (${d?.present || 0}/${d?.totalStudents || 0} present, ${
              d?.notMarked || 0
            } not marked)`;
          },
        },
      },
    },
    series: [
      { name: "Attendance %", data: attendanceDays.map((d) => d.percentage) },
    ],
  };

  const genderDonut = {
    options: {
      chart: baseChart("iskool360-gender", "donut"),
      labels: (distribution.byGender || []).map(
        (g) => GENDER_META[g.gender]?.label || g.gender
      ),
      colors: (distribution.byGender || []).map(
        (g) => GENDER_META[g.gender]?.color || "#94a3b8"
      ),
      ...donutBase("Students"),
    },
    series: genderSeries,
  };

  // Average attendance % by weekday across the 14-day window (only over days
  // that actually had attendance marked, so zero-filled holidays don't drag it down)
  const WEEKDAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const weekdayBuckets = WEEKDAY_ORDER.reduce((acc, d) => {
    acc[d] = { sum: 0, count: 0 };
    return acc;
  }, {});
  attendanceDays.forEach((d) => {
    if (d.markedCount > 0) {
      const key = moment(d.label, "DD MMM").format("ddd");
      if (weekdayBuckets[key]) {
        weekdayBuckets[key].sum += d.percentage;
        weekdayBuckets[key].count += 1;
      }
    }
  });
  const weekdayPattern = WEEKDAY_ORDER.map((label) => {
    const b = weekdayBuckets[label];
    return { label, percentage: b.count > 0 ? Math.round(b.sum / b.count) : 0 };
  });
  const hasWeekdayData = weekdayPattern.some((w) => w.percentage > 0);

  const weekdayBar = {
    options: {
      chart: baseChart("iskool360-weekday-pattern", "bar"),
      colors: [SERIES[2]],
      plotOptions: { bar: { columnWidth: "40%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: weekdayPattern.map((w) => w.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: { formatter: (v) => `${Math.round(v)}%` },
      },
      tooltip: { y: { formatter: (v) => `${v}% avg. attendance` } },
      grid: baseGrid,
    },
    series: [{ name: "Avg. attendance", data: weekdayPattern.map((w) => w.percentage) }],
  };

  const classBar = {
    options: {
      chart: baseChart("iskool360-class-strength", "bar"),
      colors: [ACCENT],
      plotOptions: {
        bar: { columnWidth: "40%", borderRadius: 4, distributed: false },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: byClass.map((c) => c.className),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { ...axisLabelStyle, rotate: -30, trim: true },
      },
      yaxis: { labels: { formatter: (v) => Math.round(v) } },
      tooltip: { y: { formatter: (v) => `${v} students` } },
      grid: baseGrid,
    },
    series: [{ name: "Students", data: byClass.map((c) => c.count) }],
  };

  return (
    <SectionShell
      id="students"
      title="Students & Attendance"
      subtitle="Enrollment and daily attendance"
      accentColor={ACCENT}
    >
      <div className="k360-grid k360-cols-2-4 k360-mb">
        <KpiStatTile
          title="Students"
          value={overview.totalStudents}
          caption={`${overview.totalParents} parents connected`}
          icon={<FaUserGraduate size={16} />}
          chipClass="bg-sky-50 text-sky-700"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Classes"
          value={overview.totalClasses}
          caption={`${overview.totalSubjects} subjects taught`}
          icon={<HiOutlineUserGroup size={17} />}
          chipClass="bg-violet-50 text-violet-600"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Present Today"
          value={attendance.today?.present || 0}
          caption={`${attendance.today?.absent || 0} absent · ${
            attendance.today?.notMarked || 0
          } not marked`}
          icon={<AiOutlineCheckCircle size={17} />}
          chipClass="bg-emerald-50 text-emerald-600"
          captionClass="text-rose-500 font-semibold"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Attendance Today"
          value={`${attendance.today?.percentage || 0}%`}
          caption={`of ${attendance.today?.totalStudents || 0} students`}
          icon={<HiOutlineClipboardCheck size={17} />}
          chipClass="bg-emerald-50 text-emerald-600"
          captionClass="text-emerald-600 font-semibold"
          accentColor={ACCENT}
        />
      </div>

      <div className="k360-grid k360-cols-3 k360-mb">
        <div className="k360-span-2">
          <Card accentColor={ACCENT}>
            <CardTitle
              title="Attendance Pulse"
              subtitle="Daily attendance percentage — last 14 days"
              extra={
                <div className="flex gap-2">
                  <Tag color="green">
                    {attendance.today?.present || 0} present today
                  </Tag>
                  <Tag color="red">
                    {attendance.today?.absent || 0} absent today
                  </Tag>
                  <Tag color="default">
                    {attendance.today?.notMarked || 0} not marked
                  </Tag>
                </div>
              }
            />
            {hasAttendanceData ? (
              <Chart
                options={attendanceArea.options}
                series={attendanceArea.series}
                type="area"
                height={260}
              />
            ) : (
              <Empty
                description="No attendance recorded in the last 14 days"
                className="py-12"
              />
            )}
          </Card>
        </div>
        <Card accentColor={ACCENT}>
          <CardTitle title="Gender Diversity" subtitle="Enrolled student split" />
          {hasGenderData ? (
            <Chart
              options={genderDonut.options}
              series={genderDonut.series}
              type="donut"
              height={260}
            />
          ) : (
            <Empty description="No students yet" className="py-12" />
          )}
        </Card>
      </div>

      <div className="k360-grid k360-cols-2">
        <Card accentColor={ACCENT}>
          <CardTitle title="Class Strength" subtitle="Students enrolled per class" />
          {hasClassData ? (
            <Chart
              options={classBar.options}
              series={classBar.series}
              type="bar"
              height={260}
            />
          ) : (
            <Empty description="No classes yet" className="py-12" />
          )}
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Weekday Attendance Pattern"
            subtitle="Average attendance % by day of week — last 14 days"
          />
          {hasWeekdayData ? (
            <Chart
              options={weekdayBar.options}
              series={weekdayBar.series}
              type="bar"
              height={260}
            />
          ) : (
            <Empty description="Not enough data yet" className="py-12" />
          )}
        </Card>
      </div>
    </SectionShell>
  );
};

export default StudentsSection;
